import * as React from 'react';
import { isPartialDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import { useIntl } from 'react-intl';
import { getLatestChanges, filterEmptyFields, IFieldWithDates } from '../utils';
import TileContentMaker from 'Components/DashboardTile/TileContentMaker';
import { filterByDocumentType, sortDocumentsDescending } from 'Utility/documentHandling';

const MotorFunctionDash = ({ documents }: IOwnProps): React.JSX.Element | undefined => {
  // First and only doc
  const motorFunctionDoc = filterEmptyFields(filterByDocumentType(documents, 'motorfunction')).at(0);

  const sortedAidDocs = filterByDocumentType(documents, 'mobilityAid').sort(sortDocumentsDescending);
  const latestAid = sortedAidDocs.at(0);

  const getAidUsageMessageId = (aid: IAid): string => {
    return aid.use ? 'motorFunctionAndMobilityAid.aids.opts.' + aid.use : 'general.empty';
  };

  const { formatMessage } = useIntl();

  let dashData = {};
  if (motorFunctionDoc) {
    const changes = getLatestChanges(motorFunctionDoc)[0] as IFieldWithDates | null;
    dashData = changes
      ? {
          [formatMessage({ id: 'motorFunctionAndMobilityAid.latestChange' })]: {
            type: 'specificListMultipleRows',
            data: [
              {
                date: isPartialDate(changes.endDate)
                  ? formatPartialDate(changes.endDate)
                  : isPartialDate(changes.startDate)
                    ? formatPartialDate(changes.startDate)
                    : '',
                title: (
                  <div>
                    <div style={{ fontSize: '1.3rem' }}>
                      {isPartialDate(changes.endDate)
                        ? formatMessage({ id: 'motorFunctionAndMobilityAid.motorfunction.abilityLostDate' })
                        : isPartialDate(changes.startDate)
                          ? formatMessage({ id: 'motorFunctionAndMobilityAid.motorfunction.abilityGainedDate' })
                          : ''}
                    </div>
                    <div style={{ fontSize: '1.6rem', fontWeight: 600 }}>
                      {formatMessage({ id: 'motorFunctionAndMobilityAid.motorfunction.' + changes.name })}
                    </div>
                  </div>
                ),
                key: 'motorFunctionDate',
              },
            ],
          },
        }
      : {};
  }

  if (latestAid) {
    dashData = {
      ...dashData,
      [formatMessage({ id: 'motorFunctionAndMobilityAid.aids.latestAidShort' })]: {
        type: 'specificListMultipleRows',
        data: [
          {
            date: isPartialDate(latestAid.startDate) ? formatPartialDate(latestAid.startDate) : '',
            title: (
              <div>
                <div
                  style={{
                    fontSize: '1.6rem',
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: isPartialDate(latestAid.endDate) ? '18rem' : '27rem',
                  }}
                >
                  {latestAid.aidType &&
                    `${formatMessage({ id: 'motorFunctionAndMobilityAid.aids.opts.' + latestAid.aidType })}`}
                  {latestAid.type &&
                    `, ${formatMessage({ id: `motorFunctionAndMobilityAid.aids.opts.${latestAid.type}` })}`}
                </div>
                <div style={{ fontSize: '1.3rem' }}>{formatMessage({ id: getAidUsageMessageId(latestAid) })}</div>
              </div>
            ),
            endDate: isPartialDate(latestAid.endDate) ? formatPartialDate(latestAid.endDate) : null,
            key: 'motorFunctionDate',
          },
        ],
      },
    };
  }

  return <TileContentMaker type="listWithSubheaders" data={dashData} />;
};

interface IOwnProps {
  documents: Array<IDocumentWithTypeV2<'motorfunction' | 'mobilityAid'>>;
}

export default MotorFunctionDash;
